import { useMemo, useState } from "react";
import { Building2, Check, ChevronsUpDown, User, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  useSalesTeams,
  useSalesTeamsMembers,
} from "@/hooks/queries/use-sales-teams";
import { cn } from "@/lib/utils";
import type { ChatScope } from "./scope";

interface Props {
  businessId: string;
  scope: ChatScope;
  onChange: (scope: ChatScope) => void;
  trigger?: "button" | "chip";
  disabled?: boolean;
  /** TEMP: omit org-wide row from the list (caller toggles, e.g. IntelligenceChat). */
  hideOrganizationOption?: boolean;
}

export function ScopePicker({
  businessId,
  scope,
  onChange,
  trigger = "button",
  disabled,
  hideOrganizationOption = false,
}: Props) {
  const { t } = useTranslation("intelligence");
  const [open, setOpen] = useState(false);
  const teamsQuery = useSalesTeams(businessId);
  const membersQuery = useSalesTeamsMembers(businessId);

  const teams = teamsQuery.data ?? [];
  const members = membersQuery.data ?? [];

  const teamById = useMemo(
    () => new Map(teams.map((t) => [t.id, t])),
    [teams],
  );

  const membersByTeam = useMemo(() => {
    const map = new Map<string | null, typeof members>();
    for (const m of members) {
      const key = m.salesTeamId ?? null;
      const list = map.get(key) ?? [];
      list.push(m);
      map.set(key, list);
    }
    return map;
  }, [members]);

  const label = useMemo(() => {
    if (scope.kind === "business") return t("scope.organization");
    if (scope.kind === "team") {
      return teamById.get(scope.salesTeamId)?.name ?? t("scope.team");
    }
    const m = members.find((mm) => mm.id === scope.memberId);
    return m?.name ?? t("scope.member");
  }, [scope, teamById, members, t]);

  const Icon =
    scope.kind === "member" ? User : scope.kind === "team" ? Users : Building2;

  const select = (next: ChatScope) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger === "chip" ? (
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50",
            )}
          >
            <Icon className="h-3 w-3" />
            <span className="max-w-[160px] truncate">{label}</span>
            <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
          </button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            className="h-8 gap-2"
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="max-w-[180px] truncate">{label}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder={t("scope.picker.search")} />
          <CommandList>
            <CommandEmpty>{t("scope.picker.noResults")}</CommandEmpty>

            {!hideOrganizationOption && (
              <CommandGroup heading={t("scope.picker.heading")}>
                <CommandItem
                  value="hela-organisationen"
                  onSelect={() => select({ kind: "business" })}
                >
                  <Building2 className="text-muted-foreground" />
                  <span className="flex-1">{t("scope.organization")}</span>
                  {scope.kind === "business" && <Check />}
                </CommandItem>
              </CommandGroup>
            )}

            {teams.map((t) => {
              const teamMembers = membersByTeam.get(t.id) ?? [];
              const isTeamActive =
                scope.kind === "team" && scope.salesTeamId === t.id;
              return (
                <CommandGroup key={t.id} heading={t.name}>
                  <CommandItem
                    value={`team-${t.name}-${t.id}`}
                    onSelect={() => select({ kind: "team", salesTeamId: t.id })}
                  >
                    <Users className="text-muted-foreground" />
                    <span className="flex-1 truncate">Visa hela teamet</span>
                    {isTeamActive && <Check />}
                  </CommandItem>
                  {teamMembers.map((m) => {
                    const isActive =
                      scope.kind === "member" && scope.memberId === m.id;
                    return (
                      <CommandItem
                        key={m.id}
                        value={`member-${m.name}-${m.id}`}
                        onSelect={() =>
                          select({
                            kind: "member",
                            memberId: m.id,
                            salesTeamId: m.salesTeamId,
                          })
                        }
                        className="pl-6"
                      >
                        <User className="text-muted-foreground" />
                        <span className="flex-1 truncate">{m.name}</span>
                        {m.title && (
                          <span className="ml-2 truncate text-xs text-muted-foreground">
                            {m.title}
                          </span>
                        )}
                        {isActive && <Check />}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              );
            })}

            {(membersByTeam.get(null) ?? []).length > 0 && (
              <CommandGroup heading="Utan team">
                {(membersByTeam.get(null) ?? []).map((m) => {
                  const isActive =
                    scope.kind === "member" && scope.memberId === m.id;
                  return (
                    <CommandItem
                      key={m.id}
                      value={`member-${m.name}-${m.id}`}
                      onSelect={() =>
                        select({
                          kind: "member",
                          memberId: m.id,
                          salesTeamId: null,
                        })
                      }
                    >
                      <User className="text-muted-foreground" />
                      <span className="flex-1 truncate">{m.name}</span>
                      {m.title && (
                        <span className="ml-2 truncate text-xs text-muted-foreground">
                          {m.title}
                        </span>
                      )}
                      {isActive && <Check />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
